// Centralized static content for ITHR Technologies
import {
  Cpu,
  Users,
  Boxes,
  Briefcase,
  Sparkles,
  ShieldCheck,
  Cloud,
  LineChart,
  Globe2,
  Compass,
  HeartHandshake,
  Rocket,
} from "lucide-react";

export const COMPANY = {
  name: "ITHR Technologies",
  legal: "ITHR Technologies Consulting LLC",
  slogan: "Humanizing Technology",
  tagline_secondary: "Consulting · Advisory · Delivery",
  founded: 2024,
  hq: "Dubai, United Arab Emirates",
  phone: "+971 43450075",
  email: "info@ithr360.com",
  careers_email: "careers@ithr360.com",
  website: "www.ithr360.com",
  whatsapp: {
    number: "447577368808",
    display: "+44 7577 368808",
    default_message:
      "Hi ITHR Technologies, I came across your website and I'd like to learn more about your services.",
  },
  address: {
    line1: "ITHR Technologies Consulting LLC",
    line2: "1105, Orchid, Dubai Science Park",
    line3: "P.O. Box 449757",
    city: "Dubai",
    country: "United Arab Emirates",
  },
  social: {
    linkedin: "https://www.linkedin.com/company/ithr360",
    twitter: "https://twitter.com/ithr360",
    instagram: "https://instagram.com/ithr360",
  },
};

export const CEO = {
  name: "Srijit Nair",
  title: "Chief Executive Officer",
  message:
    "At ITHR, we believe in pushing the boundaries of what's possible to create solutions that empower businesses and transform industries. Our commitment to innovation, collaboration, and sustainability drives everything we do — ensuring we remain at the forefront of delivering value to our clients and partners worldwide. As we continue to grow, our focus remains on fostering meaningful relationships, leveraging cutting-edge technologies, and building a future that benefits not only our organization but the communities and industries we serve.",
  closer:
    "To our clients and partners, thank you for trusting us to be part of your journey. To my team, your dedication and creativity are the foundation of our success. Together, we are building something extraordinary.",
};

export const MISSION_VISION = {
  vision:
    "We envision revolutionizing HR Outsourcing and Technology Consulting globally — to be a leading provider of transformative HR Solutions and cutting-edge IT Services, empowering organizations to thrive in an evolving business landscape.",
  mission:
    "ITHR Technologies are pioneers in HR outsourcing in the UAE and beyond, delivering innovative solutions blending AI technology and best practices. We drive organizational growth by optimizing HR functions with tailored services for businesses of all sizes.",
};

export const STATS = [
  { value: "150+", label: "Man-years of expertise" },
  { value: "16+", label: "Enterprise clients" },
  { value: "ISO", label: "9001:2015 Certified" },
  { value: "24/7", label: "Offshore delivery" },
];

export const INDUSTRIES = [
  "Healthcare",
  "Finance & Banking",
  "Telecom",
  "Manufacturing",
  "Retail",
  "Distribution",
  "FMCG",
  "Real Estate",
  "Facility Management",
  "Catering Services",
];

export const TALENT_ROADMAP = [
  { step: "01", title: "Job Description", body: "Analyse the specific requirement of the role and define it clearly with the hiring leader." },
  { step: "02", title: "AI-Enabled Job Posting", body: "Our AI applications post the JD across multiple job portals, social media and our career page." },
  { step: "03", title: "Screening & Evaluation", body: "A thorough screening and evaluation is done for every received application." },
  { step: "04", title: "Internal Interview", body: "All shortlisted profiles are interviewed in person by our panel of experts to ensure quality." },
  { step: "05", title: "Final Client Interview", body: "An evaluation report with our grading is shared with the client for final negotiation and onboarding." },
  { step: "06", title: "Reference Check & Joining", body: "The finalised resource goes through a background and credential check before joining formalities begin." },
];

export const TEAM = [
  { name: "Srijit Nair", role: "Chief Executive Officer", bio: "Founder & CEO. Sets direction across innovation, collaboration and sustainability — and the long-term partnerships that define how ITHR delivers." },
  { name: "Aravinth S", role: "Tech Talent Lead", bio: "Tech Talent Acquisition Specialist with a background in management and business administration. Strong communicator, negotiator and team leader thriving in dynamic environments." },
  { name: "Divya Sundar", role: "Talent Acquisition", bio: "Recruitment Specialist expert in end-to-end recruitment and candidate pipelines. Aligns hiring strategies with business goals and ensures timely placement of top talent." },
  { name: "Rolson Lewis", role: "SAP SD Solution Architect", bio: "Senior SAP SD Analyst — Order Management, system setup and post-implementation support. Certified SAP professional focused on process optimisation and cross-functional delivery." },
  { name: "A V Chandran", role: "HR Compliance & UAE Labor Law", bio: "24 years across HR, PRO and Administration Management. Deep expertise in employment services, payroll and UAE labour compliance across multiple industries." },
  { name: "Sailaja SL", role: "SAP HCM & SuccessFactors Solution Architect", bio: "Seasoned SAP S/4 + SuccessFactors architect with deep UAE & GCC experience at YASH Technologies, NTT and EY — specialising in SAP Payroll and SuccessFactors." },
];

export const ADVISORS = [
  { name: "Dr. Venkata Janakiram", role: "AI & Robotics Integration", bio: "Founder & CEO of Angulus IT Services. Doctorate in AI & Robotics (USA), 20+ years across Fortune 500 in healthcare, finance and telecom. Six Sigma Black Belt, ITIL Practitioner. IT Professional of the Year — Madison Who's Who, 2007." },
  { name: "Dr. Tabish Asifi", role: "ML & AI Strategy", bio: "MD at Quantz Innovations Lab & DXWiz Technology. GCC-based IT leader, 20 years across $500M+ transformation programmes. Doctorate in AI/ML adoption. IT Transformation Leadership award, 2018." },
  { name: "Dr. Vijayan Nambiar", role: "Business Strategy", bio: "CEO of A.R.K. Perfumes. 36+ years of Gulf-region marketing expertise. DBA holder, President of Toastmasters Dubai. Former COO at NMC Trading — led operational optimisation and the company's LSE listing." },
  { name: "Pavan Manchiraju", role: "Financial Strategy & Advisory", bio: "21+ years across M&A, business transformation and technology in manufacturing, retail and distribution. CFA charterholder, IIM Ahmedabad alumnus. Specialises in strategy, financial advisory and process optimisation." },
];

export const CERTIFICATIONS = [
  { code: "ISO 9001:2015", label: "Quality Management System", detail: "Certified for IT Consulting, HR Consulting & Project Management" },
  { code: "ICV", label: "In-Country Value", detail: "Registered ICV contributor in the UAE" },
  { code: "GEC 2024", label: "GEC Awards 2024", detail: "Top Vendor — HR Outsourcing Platform" },
  { code: "UKRAS", label: "UKAS-accredited body", detail: "ISO certification accredited via UKRAS" },
];

export const CLIENTS = [
  { name: "Avertra", domain: "avertra.com" },
  { name: "Emirates Investment Bank", domain: "eibank.com" },
  { name: "Tekdeliver", domain: "tekdeliver.com" },
  { name: "Scope Investment", domain: "scopeinvestment.com" },
  { name: "EO", domain: "eonetwork.org" },
  { name: "Cure Medical Centers", domain: "curemedicalcenter.ae" },
  { name: "Roboxa", domain: "roboxa.com" },
  { name: "Seidor", domain: "seidor.com" },
  { name: "YASH Technologies", domain: "yash.com" },
  { name: "Kun Sports", domain: "kunsports.com" },
  { name: "Nabil", domain: "nabilfoods.com" },
  { name: "NTT DATA", domain: "nttdata.com" },
  { name: "Federal Tax Authority", domain: "tax.gov.ae" },
  { name: "ZMI Holdings", domain: "zmiholdings.com" },
  { name: "Truspeq", domain: "truspeq.com" },
  { name: "Omorfia Group", domain: "omorfiagroup.com" },
  { name: "F9 Infotech", domain: "f9infotech.com" },
];

export const INITIATIVES = {
  academy: {
    id: "academy",
    tag: "Flagship initiative · Enterprise",
    name: "ITHR Enterprise Agentic AI Academy",
    short: "Where the world's workforce learns to command AI.",
    url: "https://learn.ithr.tech",
    established: "Est. 2026",
    origin: "Made in the UAE · for the world",
    summary:
      "A credentialing platform for enterprise workforces — ten tiers, twenty industries, one standard, refreshed continuously. From analyst to Chief AI Officer, the Academy gives every employee a proven path to AI fluency and every organization a common language for it.",
    stats: [
      { value: "10", label: "Credential tiers" },
      { value: "24", label: "Courses" },
      { value: "15", label: "Modules / course" },
      { value: "100%", label: "Publicly verifiable" },
    ],
    pillars: [
      {
        title: "Anchor to your role",
        body: "Role-based tracks for Engineers, PMs, Risk, HR, Banking, Healthcare and Executives — curriculum sequences itself for the learner.",
      },
      {
        title: "Learn with an AI Tutor",
        body: "Aletheia, a Claude-powered Socratic tutor, sits inside every lesson. Solon plans each learner's next credential and 90-day project.",
      },
      {
        title: "Prove it. Publicly.",
        body: "Cryptographically-verifiable credentials and a public AI Skills Passport — shareable on LinkedIn, résumés and enterprise directories.",
      },
      {
        title: "Curriculum that self-refreshes",
        body: "The Intelligence Desk scans the frontier every six hours — model releases, regulation, enterprise deployments — and pushes signals into your curriculum.",
      },
    ],
    ladder: [
      "Tier I — Foundation",
      "Tier II — Practitioner",
      "Tier III — Professional",
      "Tier IV — Specialist",
      "Tier V — Consultant",
      "Tier VI — Architect",
      "Tier VII — Enterprise Leader",
      "Tier VIII — Chief AI Officer",
    ],
    inspiredBy: ["Anthropic", "OpenAI", "Google DeepMind", "Microsoft", "AWS", "IBM", "Salesforce", "Palantir"],
  },
  aiilm: {
    id: "aiilm",
    tag: "Society initiative · Kids & Schools",
    name: "Sparkl by AIILM",
    short: "Where kids learn AI by playing.",
    url: "https://aiilm.me",
    established: "Live · 2026",
    origin: "GCC-rooted · Ages 10–16 · Bilingual (English & Arabic)",
    summary:
      "Sparkl turns Artificial Intelligence into a playful adventure. Children teach machines, run simulations and build their own AI agents — guided by Sparky, their AI buddy. Grounded in the values and 'ilm (علم — Arabic for knowledge) of the GCC, and elevated by technology.",
    stats: [
      { value: "10–16", label: "Age range" },
      { value: "5", label: "Big Ideas of AI" },
      { value: "2", label: "Languages (EN · AR)" },
      { value: "12", label: "Curriculum boards" },
    ],
    pillars: [
      { title: "Age-appropriate curriculum", body: "Three bands — Foundation (10–12), Builder (13–14), Innovator (15–16) — with concept-first, hands-on lessons." },
      { title: "Safety & ethics from day one", body: "The 'AI Rulebook' is built into every single lesson, so good habits form early — ad-free, kind, values-first." },
      { title: "Made for classrooms", body: "Curriculum-aligned to 12 boards including the UAE MoE. Teacher toolkits and a lightweight teacher dashboard." },
      { title: "Bilingual & culturally rooted", body: "Every course in English and Arabic. Rooted in GCC values — curiosity, respect, family — with Sparky guided to stay kind, safe and age-appropriate." },
    ],
    bands: [
      { name: "Foundation", ages: "10–12", tagline: "Explore & Understand", items: ["Voice avatar, cartoons, games", "Train a simple model", "Build a first guided agent", "Use AI safely, with guidance"] },
      { name: "Builder", ages: "13–14", tagline: "Apply & Build", items: ["Write effective prompts", "Build a working agent", "Design a multi-step workflow", "Spot bias & misinformation"] },
      { name: "Innovator", ages: "15–16", tagline: "Create & Lead", items: ["Orchestrate agents into pipelines", "Run a simulated organisation", "Ship a small AI solution", "Security, deepfakes, governance"] },
    ],
    bigIdeas: [
      { emoji: "👀", title: "AI senses the world", body: "How computers take in pictures, sound and words." },
      { emoji: "🧩", title: "AI thinks in steps", body: "How it stores what it knows and works things out." },
      { emoji: "📚", title: "AI learns from examples", body: "How examples make it better — and where bias creeps in." },
      { emoji: "💬", title: "AI talks with people", body: "How we chat with AI in words and voice." },
      { emoji: "🌍", title: "AI changes our world", body: "The good, the risky, and how to use it responsibly." },
    ],
  },
};

export const PARTNERS_NETWORK = [
  { name: "Angulus IT", role: "AI-enabled Product Engineering", domain: "angulusit.com" },  { name: "Grihasoft", role: "Data Management", domain: "grihasoft.com" },
  { name: "CDCON", role: "Cybersecurity Services", domain: "cdcon.ae" },
  { name: "SBAtech", role: "SAP Offshore Delivery", domain: "sbatech.com" },
  { name: "Darwinbox", role: "HRMS Partner", domain: "darwinbox.com" },
  { name: "Orange Ladders", role: "Data Governance", domain: "orangeladders.com" },
  { name: "DoubleTick", role: "CRM", domain: "doubletick.io" },
  { name: "Edel Tech", role: "RPA", domain: "edeltechgroup.com" },
  { name: "Connecttion", role: "Oracle Services", domain: "connecttion.com" },
];

export const SERVICES = [
  {
    id: "hr-outsourcing",
    icon: Users,
    title: "HR Outsourcing",
    tagline: "People operations, engineered.",
    description:
      "End-to-end HR delivery — payroll, compliance, performance and culture — letting you focus on your core business while we run people operations.",
    features: [
      "Payroll outsourcing (UAE & GCC, WPS compliant)",
      "Performance & goal management",
      "Training & development programs",
      "HR analytics & insights dashboards",
    ],
  },
  {
    id: "recruitment",
    icon: Briefcase,
    title: "Talent & Recruitment",
    tagline: "Hire faster, hire better.",
    description:
      "AI-enabled recruitment combined with senior recruiters embedded in your team — across C-suite head-hunting, staff augmentation and RPO.",
    features: [
      "C-Suite head-hunting (confidential)",
      "Staff augmentation & contingency",
      "Recruitment Process Outsourcing (RPO)",
      "AI-enabled sourcing across portals",
    ],
  },
  {
    id: "sap",
    icon: Boxes,
    title: "SAP Solutions",
    tagline: "End-to-end SAP, by senior architects.",
    description:
      "A senior SAP practice covering S/4HANA, SuccessFactors, FICO, MM, SD, EWM and Basis — implementation, migration, AMS and rollouts.",
    features: [
      "SAP S/4HANA implementations",
      "SAP SuccessFactors (EC, PMGM, RM, ONB)",
      "SAP HCM, FICO, MM, SD, EWM, TM, Basis",
      "Managed AMS & 24/7 offshore support",
    ],
  },
  {
    id: "it-consulting",
    icon: Cpu,
    title: "Technology Consulting",
    tagline: "Strategy that ships.",
    description:
      "From CIO advisory to platform engineering — IT strategy, custom ERP, HRIS implementation and subscription tooling, end to end.",
    features: [
      "IT strategy & advisory",
      "Custom ERP design & build",
      "HRIS implementation & integration",
      "Subscription apps & SaaS tooling",
    ],
  },
  {
    id: "ai-ml",
    icon: Sparkles,
    title: "AI & Machine Learning",
    tagline: "Applied intelligence, measurable outcomes.",
    description:
      "Custom AI solutions, copilots and automation — from proof-of-concept to governed production, with explainability and compliance built in.",
    features: [
      "Custom AI solutions & advisory",
      "AI enablement across the org",
      "Robotic Process Automation (RPA)",
      "Document AI, OCR & RAG systems",
    ],
  },
  {
    id: "digital-transformation",
    icon: Rocket,
    title: "Digital Transformation",
    tagline: "Re-platform the business.",
    description:
      "Pragmatic transformation programmes that align people, process and platform — delivered in quarters, not years.",
    features: [
      "Operating model redesign",
      "Process automation (RPA)",
      "Customer & employee experience",
      "Change management & adoption",
    ],
  },
  {
    id: "cloud",
    icon: Cloud,
    title: "Cloud & Infrastructure",
    tagline: "Multi-cloud, secure by default.",
    description:
      "Cloud-managed solutions and IT infrastructure services — including a cloud-based, AI-enabled ERP backed by 150+ man-years of expertise.",
    features: [
      "Cloud-managed services",
      "IT infrastructure services",
      "Cloud-based AI-enabled ERP",
      "Hybrid networking & identity",
    ],
  },
  {
    id: "cybersecurity",
    icon: ShieldCheck,
    title: "Cybersecurity & GRC",
    tagline: "Defend, detect, comply.",
    description:
      "Threat-informed defence aligned to ISO 27001, PCI DSS and ISO 22301 — governance, risk and compliance built around your business.",
    features: [
      "Cybersecurity engineering & SOC",
      "Governance, Risk & Compliance (GRC)",
      "Compliance: ISO 27001, PCI DSS, GDPR",
      "Business continuity & vulnerability mgmt",
    ],
  },
];

export const DIFFERENTIATORS = [
  {
    icon: HeartHandshake,
    title: "Humanizing Technology",
    body:
      "Technology is only as powerful as the people it serves. Every engagement starts with empathy and ends with outcomes.",
  },
  {
    icon: Globe2,
    title: "ISO & ICV Certified",
    body:
      "ISO 9001:2015 certified for IT Consulting, HR Consulting and Project Management. ICV-registered in the UAE.",
  },
  {
    icon: Compass,
    title: "Senior bench by default",
    body:
      "150+ man-years of combined expertise. Senior architects and consultants own every engagement end to end.",
  },
  {
    icon: LineChart,
    title: "AI-enabled delivery",
    body:
      "From AI-powered sourcing to a cloud-native AI-enabled ERP, intelligence is woven into how we deliver — not bolted on.",
  },
];

export const VALUES = [
  { title: "Innovation", body: "We push boundaries to create solutions that empower and transform." },
  { title: "Collaboration", body: "Meaningful relationships with clients, partners and our team." },
  { title: "Sustainability", body: "Building a future that benefits the communities we serve." },
  { title: "Excellence", body: "ISO-grade quality and compliance in everything we ship." },
];

export const POSITIONS = [
  { id: "sap-fico", title: "SAP FICO Consultant", location: "Dubai / Remote", type: "Full-time", department: "SAP Practice" },
  { id: "sap-hcm", title: "SAP HCM Lead Consultant", location: "Dubai / Remote", type: "Full-time", department: "SAP Practice" },
  { id: "ai-engineer", title: "AI / ML Engineer", location: "Remote", type: "Full-time", department: "AI Studio" },
  { id: "cloud-architect", title: "Cloud Solutions Architect (AWS & Azure)", location: "Dubai / Remote", type: "Full-time", department: "Cloud & Infrastructure" },
  { id: "cyber-engineer", title: "Cybersecurity Engineer", location: "Dubai", type: "Full-time", department: "Cybersecurity" },
  { id: "hr-business-partner", title: "HR Business Partner", location: "Dubai", type: "Full-time", department: "HR Outsourcing" },
  { id: "tech-recruiter", title: "Senior Technical Recruiter", location: "Remote", type: "Full-time", department: "Talent Acquisition" },
  { id: "presales-cloud", title: "Presales Lead — Cloud Technologies", location: "Dubai", type: "Full-time", department: "Cloud & Infrastructure" },
];

export const TESTIMONIALS = [
  {
    quote:
      "ITHR Technologies brought a level of senior bench depth we hadn't seen from boutique partners — they delivered our S/4HANA HR rollout on schedule and on budget.",
    author: "Group CHRO",
    company: "Industrial Conglomerate, KSA",
  },
  {
    quote:
      "Their AI team turned a six-month POC backlog into three live, governed copilots. The honesty about what not to build was as valuable as the code.",
    author: "Chief Data Officer",
    company: "Tier-1 Bank, UAE",
  },
  {
    quote:
      "What set them apart was empathy — they understood our people before they touched our systems.",
    author: "VP, People Operations",
    company: "Logistics Group, GCC",
  },
];

export const MEDIA = {
  hero: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=2000&q=80",
  dubai: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=2000&q=80",
  aiTech: "https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?auto=format&fit=crop&w=1600&q=80",
  office: "https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?auto=format&fit=crop&w=1600&q=80",
  team: "https://images.unsplash.com/photo-1452860606245-08befc0ff44b?auto=format&fit=crop&w=1600&q=80",
};

export const PARTNERS = [
  "SAP", "Microsoft Azure", "AWS", "Google Cloud", "ServiceNow", "Oracle",
  "Salesforce", "Databricks", "UiPath", "Workday", "OpenAI", "Snowflake",
];

// Apps designed, built and deployed on Emergent by ITHR Technologies.
// Live, in-production apps shipped on the Emergent platform.
export const APPS = [
  {
    id: "sparkl-aiilm",
    name: "Sparkl by AIILM",
    tagline: "Kids learn AI by playing",
    description:
      "A playful, bilingual (English & Arabic) AI-literacy programme for children aged 10–16. Kids teach machines, run simulations and build AI agents — guided by Sparky, their AI buddy — with safety and ethics baked into every lesson. GCC-rooted, curriculum-aligned to 12 boards including the UAE MoE.",
    url: "https://aiilm.me",
    cover: "https://image.thum.io/get/width/1200/crop/700/noanimate/https://aiilm.me/",
    stack: ["React", "FastAPI", "MongoDB", "AI Tutor", "Bilingual"],
    status: "Live",
    year: 2026,
    category: "EdTech · Kids",
  },
  {
    id: "skillsearch",
    name: "SkillSearch",
    tagline: "Voice-enabled AI recruiting platform",
    description:
      "A unified hiring platform built for enterprise scale — sourcing, ATS, AI interview and decisioning in one voice-enabled environment. Cuts time-to-hire by 67% and lifts recruitment efficiency by 42%.",
    url: "https://skillsearch-preview.emergent.host/",
    cover: "https://image.thum.io/get/width/1200/crop/700/noanimate/https://skillsearch-preview.emergent.host/",
    stack: ["React", "FastAPI", "MongoDB", "AI Voice", "ATS"],
    status: "Live",
    year: 2025,
    category: "HR Tech · AI",
  },
  {
    id: "identra",
    name: "Identra",
    tagline: "Your professional digital identity",
    description:
      "Create stunning digital business cards with QR codes and NFC technology. Share contact details instantly, program NFC cards in seconds, and track engagement with built-in scan analytics.",
    url: "https://identra.co/",
    cover: "https://image.thum.io/get/width/1200/crop/700/noanimate/https://identra.co/",
    stack: ["React", "FastAPI", "MongoDB", "QR", "NFC"],
    status: "Live",
    year: 2025,
    category: "Networking · SaaS",
  },
  {
    id: "nimbus-crm",
    name: "Nimbus CRM",
    tagline: "Run the room — sales control panel",
    description:
      "An internal control panel for sales operations, pipeline visibility and AI-assisted outreach. Pipelines aren't built by luck — they're built by structure.",
    url: "https://crm-connect-61.preview.emergentagent.com/",
    cover: "https://image.thum.io/get/width/1200/crop/700/noanimate/https://crm-connect-61.preview.emergentagent.com/",
    stack: ["React", "FastAPI", "MongoDB", "AI Outreach"],
    status: "Live",
    year: 2025,
    category: "Sales · CRM",
  },
  {
    id: "srijit-nair-portfolio",
    name: "Dr. Srijit Nair · Executive Portfolio",
    tagline: "Personal site of the CEO, ITHR Technologies",
    description:
      "The personal portfolio of Dr. Srijit Nair — CEO of ITHR Technologies — showcasing 20+ years across HR, IT consulting and business leadership, with an integrated AI assistant and WhatsApp chat.",
    url: "https://srijitnair.com/",
    cover: "https://image.thum.io/get/width/1200/crop/700/noanimate/https://srijitnair.com/",
    stack: ["React", "FastAPI", "MongoDB", "AI Chat"],
    status: "Live",
    year: 2025,
    category: "Portfolio",
  },
];
